import React, { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { cn } from '@ideoz/ui';
import { GLASS, RISE } from './surface';
import { hotEditStore, type HotEditBridgeApi } from '../../stores/hotEdit';
import { describeEdits, pendingEdits } from '../../preview-bridge/pendingEdits';

/** Ways out of edit mode that must not drop the founder's changes without asking. */
export const HOT_EDIT_LEAVE_REQUEST_EVENT = 'ideoz-hot-edit-leave-request';

/** Asks before leaving with changes that were never applied; the answer is the founder's. */
export function LeavePendingPrompt({ api }: { api: HotEditBridgeApi | null }): React.ReactElement | null {
  const pending = useStore(pendingEdits);
  const [asking, setAsking] = useState<(() => void) | null>(null);

  useEffect(() => {
    const onLeave = (ev: Event): void => {
      const proceed = (ev as CustomEvent<{ proceed?: () => void }>).detail?.proceed;
      if (pendingEdits.get().length === 0) {
        proceed?.();
        return;
      }
      setAsking(() => proceed ?? (() => undefined));
    };
    window.addEventListener(HOT_EDIT_LEAVE_REQUEST_EVENT, onLeave);
    return () => window.removeEventListener(HOT_EDIT_LEAVE_REQUEST_EVENT, onLeave);
  }, []);

  if (!asking || pending.length === 0) return null;

  const done = (go: boolean) => {
    const proceed = asking;
    setAsking(null);
    if (go) proceed();
  };

  const count = pending.length;
  return (
    <div className="absolute inset-0 z-50 flex items-start justify-center bg-black/20 pt-16" role="dialog" aria-modal="true">
      <div className={cn(GLASS, RISE, 'w-[300px] rounded-[14px] px-3 pb-2.5 pt-3')} data-testid="hot-edit-leave-prompt">
        <p className="text-[13.5px] font-bold leading-tight">
          Apply {count} {count === 1 ? 'change' : 'changes'}?
        </p>
        <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--editor-ink-2)' }}>
          {describeEdits(pending)}. Discarding puts the preview back as it was.
        </p>
        <div className="mt-2.5 flex items-center gap-1.5">
          <button
            type="button"
            className="h-[30px] flex-1 rounded-[9px] text-xs font-semibold text-white"
            style={{ background: 'var(--editor-accent)' }}
            onClick={() => {
              void api?.applyPendingEdits().then((ok) => ok && done(true));
            }}
          >
            Apply
          </button>
          <button
            type="button"
            className="h-[30px] flex-1 rounded-[9px] text-xs font-semibold hover:bg-black/5 dark:hover:bg-white/10"
            style={{ boxShadow: 'inset 0 0 0 1px rgba(127,127,127,0.35)' }}
            onClick={() => {
              api?.discardPendingEdits();
              done(true);
            }}
          >
            Discard
          </button>
          <button
            type="button"
            className="h-[30px] rounded-[9px] px-2 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/10"
            style={{ color: 'var(--editor-ink-2)' }}
            onClick={() => done(false)}
          >
            Keep editing
          </button>
        </div>
      </div>
    </div>
  );
}

/** Asks the prompt first, then runs `proceed` when the founder says it may. */
export function requestLeaveEditMode(proceed: () => void): void {
  if (!hotEditStore.mode.get() || pendingEdits.get().length === 0) {
    proceed();
    return;
  }
  window.dispatchEvent(new CustomEvent(HOT_EDIT_LEAVE_REQUEST_EVENT, { detail: { proceed } }));
}
